/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/

import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {ActivatedRoute, PRIMARY_OUTLET} from '@angular/router';
import {filter} from 'rxjs/operators';
import {combineLatest, Subscription} from 'rxjs';
import {Breadcrumb} from './breadcrumb.model';
import {UserService} from '../services/user.service';
import {ReplayableRouterEventsService} from '../services/replayable-router-events.service';
import {SpoSearchCriteriaService} from '../../spo/services/spo-search-criteria.service';
import {ClaimSearchCriteriaService} from '../../claim/services/claim-search-criteria.service';
import {PartNumberChangesSearchCriteriaService} from '../../part-number-changes/services/part-number-changes-search-criteria.service';
import {DependencyLoadNotifierService} from '../services/dependency-load-notifier/dependency-load-notifier.service';

@Component({
    selector: 'app-breadcrumbs',
    templateUrl: './breadcrumbs.component.html',
    styleUrls: ['./breadcrumbs.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BreadcrumbsComponent implements OnInit, OnDestroy {
    public breadcrumbs: Breadcrumb[];
    private subscriptions: Subscription = new Subscription();
    private readonly ROUTE_DATA_BREADCRUMB = 'breadcrumb';
    private breadcrumb: Breadcrumb = {
        label: 'Home',
        url: '/'
    };

    constructor(private routerService: ReplayableRouterEventsService,
                private route: ActivatedRoute,
                private userService: UserService,
                private dependencyLoadNotifier: DependencyLoadNotifierService,
                private spoSearchCriteriaService: SpoSearchCriteriaService,
                private claimSearchCriteriaService: ClaimSearchCriteriaService,
                private partNumberChangesCriteriaService: PartNumberChangesSearchCriteriaService
    ) {
    }

    ngOnInit(): void {
        const subscription = combineLatest([
            this.routerService.onNavigationEnd,
            this.dependencyLoadNotifier.onReady.pipe(filter(() => !this.userService.isFirstAccess))
        ])
                .subscribe(() => {
                    const root: ActivatedRoute = this.route.root;
                    this.breadcrumbs = this.getBreadcrumbs(root);
                    this.breadcrumbs = [this.breadcrumb, ...this.breadcrumbs];
                });

        this.subscriptions.add(subscription);
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    changeContext(url: string): void {
        if (this.breadcrumb.url === url) {
            // clear all cached filters
            this.spoSearchCriteriaService.clear();
            this.claimSearchCriteriaService.clear();
            this.partNumberChangesCriteriaService.clear();
        }
    }

    private getBreadcrumbs(route: ActivatedRoute, url: string = '', breadcrumbs: Breadcrumb[] = []): Breadcrumb[] {
        const children: ActivatedRoute[] = route.children;

        if (children.length === 0) {
            return breadcrumbs;
        }

        for (const child of children) {
            if (child.outlet !== PRIMARY_OUTLET || child.snapshot.url.length === 0) {
                continue;
            }

            if (!child.snapshot.data.hasOwnProperty(this.ROUTE_DATA_BREADCRUMB)) {
                return this.getBreadcrumbs(child, url, breadcrumbs);
            }

            const routeURL: string = child.snapshot.url.map((segment) => segment.path)
                    .join('/');

            url += `/${routeURL}`;

            const breadcrumb: Breadcrumb = {
                label: this.resolveBreadcrumbParameters(child),
                url: url
            };
            breadcrumbs.push(breadcrumb);

            return this.getBreadcrumbs(child, url, breadcrumbs);
        }
        return breadcrumbs;
    }

    private resolveBreadcrumbParameters(route: ActivatedRoute): string {
        let resolvedString = route.snapshot.data[this.ROUTE_DATA_BREADCRUMB];

        route.snapshot.paramMap.keys.forEach((key) => {
            resolvedString = resolvedString.replace(`:${key}`, route.snapshot.params[key]);
        });

        return resolvedString;
    }
}
